import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { X, Banknote, Loader2 } from 'lucide-react';

// ---------------------------------------------------------------------
// Record Salaries dialog (used by ExpenseTracker).
//
// Collects three things: salary month, salary year and the total salaries
// paid for that period. It does not talk to the backend itself - it hands
// { month, year, totalSalaries } to `onSave` (async-friendly) and closes
// when that resolves. If `onSave` throws, the dialog stays open and shows
// the error so nothing is lost. The parent saves it as an office expense
// (category/item "Salaries", dated the last day of the salary month); the
// info box below just previews those defaults.
//
// `month` is 1-12 (matches JS Date months + 1).
// ---------------------------------------------------------------------

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Current year +/- a small window; plenty for recording payroll.
const buildYears = () => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2, y - 3];
};

const RecordSalariesDialog = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [total, setTotal] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset to sensible defaults each time the dialog opens.
  useEffect(() => {
    if (open) {
      const now = new Date();
      setMonth(now.getMonth() + 1);
      setYear(now.getFullYear());
      setTotal('');
      setError('');
      setSaving(false);
    }
  }, [open]);

  if (!open) return null;

  const amount = parseFloat(total);
  const isValid = !Number.isNaN(amount) && amount > 0;

  const handleSubmit = async () => {
    if (!isValid) {
      setError('Enter a total salaries amount greater than 0');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ month, year, totalSalaries: amount });
      onClose();
    } catch (e) {
      setError(e?.message || 'Failed to record salaries');
    } finally {
      setSaving(false);
    }
  };

  const labelCls = `block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const fieldCls = `w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark
      ? 'bg-[#2b323c] border-gray-700 text-gray-100'
      : 'bg-white border-gray-300 text-gray-900'
  }`;

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50 p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        className={`w-full max-w-md rounded-xl border shadow-xl ${
          isDark ? 'bg-[#1e242c] border-gray-700' : 'bg-white border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
              <Banknote className="w-5 h-5" />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Record Salaries</h3>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total salaries paid for a month
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Salary Month</label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={fieldCls}>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Salary Year</label>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={fieldCls}>
                {buildYears().map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Total Salaries (KSh)</label>
            <div className="relative">
              <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>KSh</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder="0.00"
                autoFocus
                className={`${fieldCls} pl-12`}
              />
            </div>
          </div>

          {/* Defaults the expense will be saved with */}
          <div className={`rounded-lg border px-3 py-2.5 text-sm space-y-1 ${isDark ? 'border-gray-700 bg-[#2b323c] text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
            <div className="flex justify-between"><span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Category</span><span className="font-medium">Salaries</span></div>
            <div className="flex justify-between"><span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Item</span><span className="font-medium">Salaries</span></div>
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Expense date</span>
              <span className="font-medium">
                {new Date(year, month, 0).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-end gap-2 px-5 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            disabled={saving}
            className={`px-4 py-2 border rounded-lg transition-colors disabled:opacity-50 ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Record Salaries
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordSalariesDialog;